import React from "react";

import Table from "@cloudscape-design/components/table";
import Box from "@cloudscape-design/components/box";
import Header from "@cloudscape-design/components/header";
import Link from "@cloudscape-design/components/link";
import Alert from "@cloudscape-design/components/alert";
import Button from "@cloudscape-design/components/button";
import Pagination from "@cloudscape-design/components/pagination";
import CollectionPreferences from "@cloudscape-design/components/collection-preferences";
import Multiselect from "@cloudscape-design/components/multiselect";
import Select from "@cloudscape-design/components/select";
import Input from "@cloudscape-design/components/input";
import Badge from "@cloudscape-design/components/badge";
import { SpaceBetween } from "@cloudscape-design/components";
import Grid from "@cloudscape-design/components/grid";
import FormField from "@cloudscape-design/components/form-field";

/* Recoil */
import { useRecoilValue, useSetRecoilState } from "recoil";
import { staffStore } from "../stores/staff";
import { configStore } from "../stores/config";
import { staffAddModalStore } from "../stores/staffAddModal";
import { staffDetailModalStore } from "../stores/staffDetailModal";
import { userStore } from "../stores/access";
/* ------- */

const STAFF_STATUS_OPTIONS = [
	{ label: "Active", value: "active" },
	{ label: "Inactive", value: "inactive" },
	{ label: "All", value: "all" },
];

class Staff extends React.Component {
	constructor(props) {
		super(props);
		this.state = { searchInputFocused: false };
		this.staffLoadRequestId = 0;
		this.loadVisibleProjects(true);
	}

	componentDidUpdate(prevProps) {
		if (prevProps.staff.reloadSignal !== this.props.staff.reloadSignal) {
			this.loadStaff();
		}
		if (prevProps.user.login !== this.props.user.login) {
			this.loadVisibleProjects(true);
		}
	}

	normalizeBrandIds(brands) {
		const seen = new Set();
		return (Array.isArray(brands) ? brands : [])
			.map((brand) => Number(brand))
			.filter((brand) => Number.isFinite(brand))
			.filter((brand) => {
				if (seen.has(brand)) return false;
				seen.add(brand);
				return true;
			});
	}

	resolveBrandName(brand) {
		const brandId = Number(brand);
		const list = this.props.user.brandsList || [];
		const match = list.find((item) => {
			if (typeof item === "string") return Number(item) === brandId;
			const itemBrand = typeof item.brand !== "undefined" ? item.brand : typeof item.id !== "undefined" ? item.id : item.value;
			return Number(itemBrand) === brandId;
		});

		if (!match) return String(brandId);
		return typeof match === "string" ? match : match.brandName || match.name || match.label || String(brandId);
	}

	normalizeVisibleProjects(projects) {
		const seen = new Map();
		(Array.isArray(projects) ? projects : []).forEach((project) => {
			const brand = Number(project.brand);
			if (!Number.isFinite(brand) || seen.has(brand)) return;
			seen.set(brand, { brand, brandName: project.brandName || this.resolveBrandName(brand) });
		});
		return Array.from(seen.values());
	}

	filterBrandsByVisibleProjects(brands, visibleProjects = this.props.staff.visibleProjects || []) {
		const visibleBrandIds = new Set((visibleProjects || []).map((project) => Number(project.brand)).filter((brand) => Number.isFinite(brand)));
		return this.normalizeBrandIds(brands).filter((brand) => visibleBrandIds.has(brand));
	}

	getPageLimit(limit = null) {
		const nextLimit = Number(limit !== null ? limit : (this.props.staff.pagination || {}).limit || this.props.config.staff.perPage);
		return Math.floor(Number.isFinite(nextLimit) && nextLimit > 0 ? nextLimit : Number(this.props.config.staff.perPage) || 20);
	}

	getBackendSorting(sorting = this.props.staff.sorting) {
		const sortingState = sorting || {};
		const sortingColumn = sortingState.sortingColumn || {};
		const columnMap = {
			login: "login",
			user: "login",
			level: "level",
			projects: "totalProjects",
			totalProjects: "totalProjects",
			lastOnline: "lastOnline",
			created: "created",
		};
		const column = columnMap[sortingColumn.sortingField] || columnMap[sortingColumn.id] || columnMap[sortingState.column];

		if (!column) return null;

		return {
			column,
			order: sortingState.isDescending || sortingState.sortingDescending || sortingState.order === "DESC" ? "DESC" : "ASC",
		};
	}

	loadVisibleProjects(loadStaffAfter = false) {
		const login = this.props.user.login;

		if (!login) {
			this.props.setStaff((staff) => ({
				...staff,
				visibleProjects: [],
				visibleProjectsLoaded: true,
				visibleProjectsError: null,
				visibleProjectsLogin: login,
			}));
			if (loadStaffAfter) this.loadStaff();
			return;
		}

		this.props.setStaff((staff) => ({
			...staff,
			visibleProjects: [],
			visibleProjectsLoaded: false,
			visibleProjectsError: null,
			visibleProjectsLogin: login,
		}));

		this.props.Interface.request("get", { class: "settings", module: "staff", method: "person" }, { login })
			.then((data) => {
				if (this.props.user.login !== login) return;

				const visibleProjects = this.normalizeVisibleProjects(data && data.projects);
				const currentFilter = this.props.staff.filter || { brands: [], search: "", status: "active" };
				const currentBrands = this.normalizeBrandIds(currentFilter.brands);
				const prunedBrands = this.filterBrandsByVisibleProjects(currentBrands, visibleProjects);
				const brandsChanged = currentBrands.length !== prunedBrands.length || currentBrands.some((brand, index) => brand !== prunedBrands[index]);

				this.props.setStaff((staff) => ({
					...staff,
					visibleProjects,
					visibleProjectsLoaded: true,
					visibleProjectsError: null,
					visibleProjectsLogin: login,
					filter: {
						...(staff.filter || { brands: [], search: "", status: "active" }),
						brands: prunedBrands,
					},
					pagination: brandsChanged ? { ...staff.pagination, current: 1 } : staff.pagination,
				}));

				if (loadStaffAfter || currentBrands.length > 0) {
					this.loadStaff({ brands: prunedBrands, search: currentFilter.search, visibleProjects, page: brandsChanged ? 1 : null });
				}
			})
			.catch((e) => {
				if (this.props.user.login !== login) return;

				this.props.setStaff((staff) => ({
					...staff,
					visibleProjects: [],
					visibleProjectsLoaded: true,
					visibleProjectsError: e,
					visibleProjectsLogin: login,
					filter: {
						...(staff.filter || { brands: [], search: "", status: "active" }),
						brands: [],
					},
					pagination: { ...staff.pagination, current: 1 },
				}));

				if (loadStaffAfter) this.loadStaff({ brands: [], visibleProjects: [], page: 1 });
			});
	}

	loadStaff({ brands = null, search = null, status = null, page = null, limit = null, sorting = null, visibleProjects = null } = {}) {
		this.staffLoadRequestId += 1;
		const requestId = this.staffLoadRequestId;
		const currentFilter = this.props.staff.filter || { brands: [], search: "", status: "active" };
		const requestedBrandIds = brands !== null ? this.normalizeBrandIds(brands) : this.normalizeBrandIds(currentFilter.brands);
		const visibleProjectsReady = visibleProjects !== null || (this.props.staff.visibleProjectsLoaded && !this.props.staff.visibleProjectsError);
		const brandIds = visibleProjectsReady ? this.filterBrandsByVisibleProjects(requestedBrandIds, visibleProjects || this.props.staff.visibleProjects) : [];
		const searchStr = search !== null ? search : currentFilter.search || "";
		const statusValue = status !== null ? status : currentFilter.status || "active";
		const limitValue = this.getPageLimit(limit);
		const pageValue = Math.max(1, Math.floor(Number(page !== null ? page : (this.props.staff.pagination || {}).current || 1) || 1));
		const backendSorting = this.getBackendSorting(sorting || this.props.staff.sorting);

		const payload = {
			status: statusValue,
			page: pageValue,
			limit: limitValue,
		};
		if (Array.isArray(brandIds) && brandIds.length > 0) payload.brands = brandIds;
		if (typeof searchStr === "string" && searchStr.length > 0) payload.search = searchStr;
		if (backendSorting) payload.sorting = backendSorting;

		this.props.setStaff((staff) => ({ ...staff, loaded: false }));

		this.props.Interface.request("get", { class: "data", module: "info", method: "staff/global" }, payload)
			.then((data) => {
				if (requestId !== this.staffLoadRequestId) return;

				if (!data || !Array.isArray(data.items) || !data.pagination || typeof data.pagination !== "object" || Array.isArray(data.pagination)) {
					throw { text: "Invalid staff list response", code: 0, timestamp: Date.now() };
				}

				const items = data.items;
				const responsePagination = data.pagination;
				const responsePage = Number(responsePagination.page);
				const responseLimit = Number(responsePagination.limit);
				const responseTotal = Number(responsePagination.total);
				const responsePages = Number(responsePagination.pages);
				const totalItems = Number.isFinite(responseTotal) ? responseTotal : items.length;
				const totalPages = Number.isFinite(responsePages) ? responsePages : Math.max(1, Math.ceil(totalItems / limitValue));
				const currentPage = Math.max(1, Math.floor(Number.isFinite(responsePage) ? responsePage : pageValue));
				const currentLimit = Math.floor(Number.isFinite(responseLimit) && responseLimit > 0 ? responseLimit : limitValue);
				const nextSorting = backendSorting
					? {
							...(sorting || this.props.staff.sorting),
							column: backendSorting.column,
							order: backendSorting.order,
							isDescending: backendSorting.order === "DESC",
							sortingDescending: backendSorting.order === "DESC",
					  }
					: sorting || this.props.staff.sorting;

				this.props.setStaff((staff) => ({
					...staff,
					data: items,
					loaded: true,
					error: { text: "", code: 0, timestamp: 0 },
					pagination: {
						current: currentPage,
						total: totalItems,
						count: totalItems,
						pages: Math.max(1, totalPages),
						limit: currentLimit,
					},
					filter: { brands: requestedBrandIds, search: searchStr, status: statusValue },
					sorting: nextSorting,
				}));
			})
			.catch((e) => {
				if (requestId !== this.staffLoadRequestId) return;

				this.props.setStaff((staff) => ({ ...staff, loaded: true, error: e }));
			});
	}

	formatLastOnline(lastOnline) {
		if (!lastOnline) {
			return (
				<Box variant="span" color="text-status-error">
					Never
				</Box>
			);
		}
		if (Date.now() - lastOnline < 300000) {
			return (
				<Box variant="span" color="text-status-success">
					Online
				</Box>
			);
		}
		return new Date(lastOnline).toLocaleTimeString() + " " + new Date(lastOnline).toLocaleDateString();
	}

	brandOptions() {
		if (!this.props.staff.visibleProjectsLoaded || this.props.staff.visibleProjectsError) return [];
		return (this.props.staff.visibleProjects || []).map((project) => ({
			label: project.brandName || this.resolveBrandName(project.brand),
			value: String(project.brand),
		}));
	}

	render() {
		const filter = this.props.staff.filter || { brands: [], search: "", status: "active" };
		const status = filter.status || "active";
		const pagination = this.props.staff.pagination || {};
		const pageSize = this.getPageLimit();
		const items = this.props.staff.data || [];
		const totalItemsValue = Number(pagination.total);
		const totalItems = Number.isFinite(totalItemsValue) ? totalItemsValue : items.length;
		const pageCountValue = Number(pagination.pages);
		const pageCount = Math.max(1, Number.isFinite(pageCountValue) ? pageCountValue : Math.ceil(totalItems / pageSize));
		const currentPage = Math.min(Number(pagination.current) || 1, pageCount);
		const sortingState = this.props.staff.sorting || {};
		const sortingColumn = sortingState.sortingColumn || { sortingField: sortingState.column || "login" };
		const sortingDescending = sortingState.isDescending || sortingState.sortingDescending || sortingState.order === "DESC";
		const tableMinHeight = Math.min(totalItems, pageSize) > 0 ? `${Math.min(totalItems, pageSize) * 45 + 260}px` : undefined;

		const brandOptions = this.brandOptions();
		const selectedBrandOptions = brandOptions.filter((o) => (filter.brands || []).indexOf(Number(o.value)) !== -1);
		const selectedStatusOption = STAFF_STATUS_OPTIONS.find((o) => o.value === status) || STAFF_STATUS_OPTIONS[0];

		return (
			<>
				{this.props.staff.loaded && this.props.staff.error.text.length ? (
					<Box margin={{ bottom: "m" }}>
						<Alert type="error" header="Error loading data:">
							<Box variant="p" children={this.props.staff.error.text} />
							<hr />
							<Box variant="p">
								request error code: {this.props.staff.error.code} | request timestamp: {this.props.staff.error.timestamp}
							</Box>
						</Alert>
					</Box>
				) : (
					<></>
				)}

				<style>
					{`
						.staff-viewer-fixed-table table {
							table-layout: fixed;
							width: 100%;
						}
						.staff-viewer-fixed-table th:nth-child(1),
						.staff-viewer-fixed-table td:nth-child(1) {
							width: 22%;
						}
						.staff-viewer-fixed-table th:nth-child(2),
						.staff-viewer-fixed-table td:nth-child(2) {
							width: 7%;
						}
						.staff-viewer-fixed-table th:nth-child(3),
						.staff-viewer-fixed-table td:nth-child(3) {
							width: 43%;
						}
						.staff-viewer-fixed-table th:nth-child(4),
						.staff-viewer-fixed-table td:nth-child(4) {
							width: 7%;
						}
						.staff-viewer-fixed-table th:nth-child(5),
						.staff-viewer-fixed-table td:nth-child(5) {
							width: 13%;
						}
						.staff-viewer-fixed-table th:nth-child(6),
						.staff-viewer-fixed-table td:nth-child(6) {
							width: 8%;
						}
					`}
				</style>
				<div className="staff-viewer-fixed-table" style={{ minHeight: tableMinHeight }}>
					<Table
					pagination={
						<Pagination
							disabled={!this.props.staff.loaded}
							currentPageIndex={currentPage}
							pagesCount={pageCount}
							onChange={(page) => {
								const nextPage = page.detail.currentPageIndex;
								this.props.setStaff({ ...this.props.staff, pagination: { ...this.props.staff.pagination, current: nextPage } });
								this.loadStaff({ page: nextPage });
							}}
						/>
					}
					onSortingChange={(sorting) => {
						const backendSorting = this.getBackendSorting(sorting.detail);
						const nextSorting = backendSorting
							? {
									...sorting.detail,
									column: backendSorting.column,
									order: backendSorting.order,
									isDescending: backendSorting.order === "DESC",
									sortingDescending: backendSorting.order === "DESC",
							  }
							: sorting.detail;
						this.props.setStaff({ ...this.props.staff, sorting: nextSorting });
						this.loadStaff({ sorting: nextSorting });
					}}
					columnDefinitions={[
						{
							id: "user",
							sortingField: "login",
							header: "User",
							width: "22%",
							cell: (item) => (
								<div style={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									<Link
										onFollow={() => {
											this.props.setStaffDetailModal({
												...this.props.staffDetailModal,
												show: true,
												login: item.login,
												loaded: false,
												person: null,
												dirty: false,
												activeTabId: "overview",
												error: { text: "", code: 0, timestamp: 0 },
											});
										}}>
										{item.login}
									</Link>
								</div>
							),
						},
						{ id: "level", sortingField: "level", header: "Level", width: "7%", cell: (item) => item.level },
						{
							id: "projects",
							sortingField: "totalProjects",
							header: "Projects",
							width: "43%",
							cell: (item) => {
								const names = (item.projects || []).map((p) => p.brandName).filter(Boolean).join(", ");
								return (
									<div title={names} style={{ display: "block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
										{names}
									</div>
								);
							},
						},
						{
							id: "tfa",
							header: "2FA",
							width: "7%",
							cell: (item) => (item.hasTfa ? <Badge color="green">Yes</Badge> : <Badge color="grey">No</Badge>),
						},
						{
							id: "lastOnline",
							sortingField: "lastOnline",
							header: "Last online",
							width: "13%",
							cell: (item) => this.formatLastOnline(item.lastOnline),
						},
						{
							id: "created",
							sortingField: "created",
							header: "Created",
							width: "8%",
							cell: (item) => (item.created ? new Date(item.created).toLocaleDateString() : ""),
						},
					]}
					items={items}
					loading={!this.props.staff.loaded}
					sortingColumn={sortingColumn}
					sortingDescending={sortingDescending}
					preferences={
						<CollectionPreferences
							title="Preferences"
							confirmLabel="Confirm"
							cancelLabel="Cancel"
							preferences={{ pageSize }}
							pageSizePreference={{
								title: "Rows per page",
								options: [
									{ value: 10, label: "10" },
									{ value: 20, label: "20" },
									{ value: 30, label: "30" },
									{ value: 40, label: "40" },
									{ value: 50, label: "50" },
								],
							}}
							onConfirm={(e) => {
								const nextLimit = this.getPageLimit(e.detail.pageSize);
								this.props.setStaff({ ...this.props.staff, pagination: { ...this.props.staff.pagination, current: 1, limit: nextLimit } });
								this.loadStaff({ page: 1, limit: nextLimit });
							}}
						/>
					}
					filter={
						<Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
							<FormField label="User">
								<Input
									type="search"
									autoComplete="new-password"
									name="staff-table-filter-text"
									readOnly={!this.state.searchInputFocused}
									value={filter.search || ""}
									placeholder="Search by login"
									onFocus={() => {
										this.setState({ searchInputFocused: true });
									}}
									onBlur={() => {
										this.setState({ searchInputFocused: false });
									}}
									onChange={(e) => {
										if (!this.state.searchInputFocused) return;
										this.props.setStaff({
											...this.props.staff,
											filter: { ...filter, search: e.detail.value },
											pagination: { ...this.props.staff.pagination, current: 1 },
										});
										this.loadStaff({ search: e.detail.value, page: 1 });
									}}
								/>
							</FormField>
							<FormField label="Brands">
								<Multiselect
									placeholder={this.props.staff.visibleProjectsLoaded ? "All brands" : "Loading brands..."}
									options={brandOptions}
									selectedOptions={selectedBrandOptions}
									disabled={!this.props.staff.visibleProjectsLoaded || !!this.props.staff.visibleProjectsError}
									statusType={!this.props.staff.visibleProjectsLoaded ? "loading" : this.props.staff.visibleProjectsError ? "error" : "finished"}
									errorText="Error loading brands"
									onChange={(e) => {
										const nextIds = (e.detail.selectedOptions || []).map((o) => Number(o.value));
										this.props.setStaff({
											...this.props.staff,
											filter: { ...filter, brands: nextIds },
											pagination: { ...this.props.staff.pagination, current: 1 },
										});
										this.loadStaff({ brands: nextIds, search: filter.search, page: 1 });
									}}
									deselectAriaLabel={(o) => `Remove ${o.label}`}
									tokenLimit={2}
									empty="No brands available"
								/>
							</FormField>
							<FormField label="Status">
								<Select
									selectedOption={selectedStatusOption}
									options={STAFF_STATUS_OPTIONS}
									onChange={(e) => {
										this.props.setStaff({
											...this.props.staff,
											filter: { ...filter, status: e.detail.selectedOption.value },
											pagination: { ...this.props.staff.pagination, current: 1 },
										});
										this.loadStaff({ status: e.detail.selectedOption.value, page: 1 });
									}}
								/>
							</FormField>
						</Grid>
					}
					header={
						<Box margin={{ bottom: "m" }}>
							<Header
								description="Unified view across all your brands"
								counter={`(${totalItems})`}
								actions={
									this.props.user.level >= 6 ? (
										<SpaceBetween direction="horizontal" size="s">
											<Button
												onClick={() => {
													this.props.setStaffAddModal({ ...this.props.staffAddModal, show: true });
												}}
												variant="primary">
												Add user
											</Button>
										</SpaceBetween>
									) : null
								}>
								Staff viewer
							</Header>
						</Box>
					}
					empty="No staff found"
					/>
				</div>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const user = useRecoilValue(userStore);
		const staff = useRecoilValue(staffStore);
		const setStaff = useSetRecoilState(staffStore);
		const config = useRecoilValue(configStore);
		const staffAddModal = useRecoilValue(staffAddModalStore);
		const setStaffAddModal = useSetRecoilState(staffAddModalStore);
		const staffDetailModal = useRecoilValue(staffDetailModalStore);
		const setStaffDetailModal = useSetRecoilState(staffDetailModalStore);

		return (
			<Component
				{...props}
				user={user}
				staffAddModal={staffAddModal}
				setStaffAddModal={setStaffAddModal}
				staffDetailModal={staffDetailModal}
				setStaffDetailModal={setStaffDetailModal}
				config={config}
				staff={staff}
				setStaff={setStaff}
			/>
		);
	};
}

export default withRecoil(Staff);
